<script lang="ts" setup>
import { useArrayAdd, useArrayRemove, useArrayReset, useArrayUpdate } from "@/composables/use-array";
import { useHousingPropertyGroups } from "@/models/HousingProperty";
import usePropertyWhitelist from "@/composables/use-property-whitelist";

const emit = defineEmits<{
  (event: "update:value", values: number[]): void;
  (event: "reset", values: number[]): void;
  (event: "add", value: number): void;
  (event: "delete", value: number): void;
}>();

const props = withDefaults(
  defineProps<{
    type?: "checkbox" | "badge";
    disabled?: boolean;
    value?: number[];
    default?: number[];
    groups: number | number[];
  }>(),
  {
    type: "checkbox",
    disabled: false,
    value: () => [],
    default: () => [],
  }
);

const baseStore = useBaseStore();
const { getWhitelist } = usePropertyWhitelist();

const update = useArrayUpdate<number>(emit, props);
const reset = useArrayReset<number>(emit, props, update);
const add = useArrayAdd<number>(emit, props, update);
const remove = useArrayRemove<number>(emit, props, update);

const housingPropertyGroups = useHousingPropertyGroups({ baseStore });
const whitelist = getWhitelist();

const groups = computed(() => housingPropertyGroups.items.filter((group) => (Array.isArray(props.groups) ? props.groups : [props.groups]).includes(group.id)));
const properties = computed(() =>
  groups.value
    .map((group) => group.getProperties())
    .flat()
    .filter((property) => whitelist.includes(property.id))
);

defineExpose({ groups, properties, update, reset, add, remove });
</script>

<template>
  <ul :class="['tw-checkbox-list filter--housing-properties', props.type === 'badge' ? 'badge-mode' : '']">
    <li v-for="item in properties" :key="`TwFilter_HousingProperties_${item.id}`" class="tw-checkbox-list--item">
        <tw-ui-checkbox v-if="props.type === 'checkbox'" :disabled="props.disabled" :state="props.value.includes(item.id)" @check="add(item.id)" @uncheck="remove(item.id)">
        {{ item.text("name") }}
      </tw-ui-checkbox>
      <tw-ui-badge
        @click="props.disabled ? null : props.value.includes(item.id) ? remove(item.id) : add(item.id)"
        :class="props.value.includes(item.id) ? '' : 'inactive'"
        v-else>
        {{ item.text("name") }}
      </tw-ui-badge>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/tw-variables.scss";

.tw-checkbox-list {
  display: flex;
  flex-flow: column nowrap;
  gap: $tw-component--gap;
  padding: 0;
  list-style-type: none;
  &.badge-mode {
    display: flex;
    flex-wrap: wrap;
    flex-flow: row wrap;
  }
}

.tw-badge {
  &.inactive {
    background-color: transparent;
    border: 1px solid $lightGray;
    color: $gray;
  }
}

.tw-checkbox {
  cursor: pointer;
}
</style>
